import React, { useCallback, useMemo } from 'react';
import noop from 'lodash/noop';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { BaseIconButton, Stack, Tooltip, Typography, TypographyColors } from '@lyearn/ui';
import { FilterOption } from '@/components/Filter/types';
import stopPropagation from '@/consts/stopPropogation';
import TagValueSelector from '@/modules/corporate/modules/tags/components/TagValueSelector';
import { getDateTagPlaceholder } from '@/modules/corporate/modules/tags/components/TagValueSelector/utils';
import {
  DateValue,
  SelectedTagValues,
  TagInputProps,
  TagsMenuItem,
  TagValues,
} from '@/modules/corporate/modules/tags/types';
import { adaptTagsOnEdit, adaptTagsOnSubmit } from '@/modules/corporate/modules/tags/utils';
import { baseButtonFocusStyles } from '@/modules/tasks/components/TaskStatus/styles';
import { DateTagType, EntityTagsInput, EntityTagType, TagTypeEnum } from '@/types/schema';

import { TagPaginatedFilters } from '../TagValueSelector/types';
import { containerStyles } from './styles';

interface AttachedTagsContainerProps {
  tag: EntityTagType;
  /**
   * Need all tags so that can provide the adapted tags for mutation call
   * else will need the same adaption logic wherever it is used
   */
  tags: EntityTagType[];
  onUpdateTag: (tags?: EntityTagsInput | null) => void;
  styles?: {
    color?: TypographyColors;
    truncate?: boolean;
  };
  filters?: TagPaginatedFilters;
  disableInteractiveTooltip?: boolean;
}

const AddTagButton = ({
  disableInteractiveTooltip = false,
}: {
  disableInteractiveTooltip?: boolean;
}) => {
  const { t } = useTranslation('Tags');
  return (
    <Stack className="border border-transparent rounded-8 px-10 hover:border-border hover:bg-transparent active:border-primary py-10 w-full flex">
      <BaseIconButton
        aria-label="priority"
        className={cn(
          'p-0 space-s-8 hover:bg-transparent h-full w-full flex justify-start px-0',
          baseButtonFocusStyles,
        )}>
        <Tooltip disableInteractive={disableInteractiveTooltip} title={t('Tags:Form.AddTag')}>
          <span className="text-text-placeholder">-</span>
        </Tooltip>
      </BaseIconButton>
    </Stack>
  );
};

export const renderTagValue = (tag: TagsMenuItem, tagValue?: TagValues) => {
  if (!tagValue) {
    return '';
  }

  switch (tag.type) {
    case TagTypeEnum.Date:
      return getDateTagPlaceholder(tag as DateTagType, tagValue as DateValue);
    case TagTypeEnum.SingleSelect:
    case TagTypeEnum.User:
    case TagTypeEnum.MultiSelect: {
      const selectedLabels = (tagValue as FilterOption[]).map((val) => val.label);
      return selectedLabels.join(', ');
    }
    case TagTypeEnum.Number:
    case TagTypeEnum.Text:
      return tagValue.toString();
    default:
      return '';
  }
};

function SelectedTag(props: AttachedTagsContainerProps) {
  const { t } = useTranslation('Tags');
  const { onUpdateTag, styles = {}, filters, disableInteractiveTooltip = false } = props;

  const { color = 'text-text-secondary', truncate = true } = styles;

  const adaptedTags = useMemo(() => adaptTagsOnEdit(props.tags), [props.tags]);

  const onTagsValueChange = useCallback(
    (val: SelectedTagValues) => {
      const updatedTag = {
        ...adaptedTags,
        tagValues: val,
      } as TagInputProps;
      onUpdateTag(adaptTagsOnSubmit(updatedTag));
    },
    [adaptedTags, onUpdateTag],
  );

  const inViewTag = useMemo(
    () => adaptedTags?.tags.find((tag) => tag.id === props.tag.tagId),
    [adaptedTags?.tags, props.tag.tagId],
  );

  const hasTagValue = useMemo(() => {
    if (!inViewTag || !adaptedTags?.tagValues) return false;

    const tagValue = adaptedTags.tagValues[inViewTag.id];
    switch (inViewTag.type) {
      case TagTypeEnum.User:
      case TagTypeEnum.MultiSelect:
      case TagTypeEnum.SingleSelect:
        return (tagValue as FilterOption[])?.length > 0;
      case TagTypeEnum.Number: {
        return typeof tagValue === 'number';
      }
      default:
        return Boolean(tagValue);
    }
  }, [inViewTag, adaptedTags?.tagValues]);

  if (!inViewTag) {
    return null;
  }

  return (
    <Stack className="w-full h-full" onClick={stopPropagation}>
      <TagValueSelector
        className="w-full"
        filters={filters}
        readOnly={false}
        selectedTagValues={adaptedTags?.tagValues}
        tag={inViewTag}
        onDeleteTag={noop}
        onTagsValueChange={onTagsValueChange}>
        <Stack className="w-full h-full">
          {hasTagValue ? (
            <Stack
              alignItems="center"
              className={cn(
                containerStyles,
                'cursor-pointer border border-transparent rounded-8 px-10 hover:border-border hover:bg-transparent active:border-primary py-10 w-full flex',
                {
                  'h-40 truncate': truncate,
                  'py-8': !truncate,
                },
              )}>
              <Typography
                className={truncate ? 'truncate' : ''}
                color={color}
                variant="body-short-02">
                {renderTagValue(inViewTag, adaptedTags?.tagValues[inViewTag.id])}
              </Typography>
            </Stack>
          ) : (
            <AddTagButton disableInteractiveTooltip={disableInteractiveTooltip} />
          )}
        </Stack>
      </TagValueSelector>
    </Stack>
  );
}

export default SelectedTag;
